export interface ChangelogItem {
  type: 'feature' | 'fix' | 'improvement';
  text: string;
}

export interface ChangelogEntry {
  version: string;
  date: string;
  items: ChangelogItem[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '2.7.1',
    date: '2026-10-01',
    items: [
      { type: 'improvement', text: 'Cleaner toolbar on phones: refresh, light and dark mode, Pro, share, settings, and sign out now live in the ⋯ menu so nothing gets cut off' },
    ],
  },
  {
    version: '2.7.0',
    date: '2026-10-01',
    items: [
      { type: 'feature', text: 'Weekly check-in streaks: any edit you make counts as your check-in for the week, and the flame in the header tracks your streak. Nothing to update? Tap "Everything\'s up to date" instead' },
      { type: 'feature', text: 'Streak freeze covers one missed week automatically, and recharges after four more weeks of checking in' },
      { type: 'feature', text: 'Confetti and a celebration at 4, 8, 12, 26, 52, and 104 weeks in a row' },
    ],
  },
  {
    version: '2.6.2',
    date: '2026-10-01',
    items: [
      { type: 'feature', text: 'Features, Pricing, and Sign In now have their own pages at tallyup.finance/features, /pricing, and /login' },
      { type: 'improvement', text: 'Search engines and link previews now see the full landing page content, with a sitemap and clearer page titles and descriptions' },
    ],
  },
  {
    version: '2.6.1',
    date: '2026-09-29',
    items: [
      { type: 'fix', text: 'Crypto prices fall back to Coinbase when CoinGecko blocks or rate-limits requests, so holdings no longer show $0' },
      { type: 'fix', text: 'Daily net worth snapshot waits until every crypto holding has a live price, so history never records crypto at $0' },
      { type: 'fix', text: "Today's net worth snapshot now refreshes each time you open TallyUp, so an early low reading corrects itself instead of sticking for the day" },
      { type: 'improvement', text: 'Net worth no longer dips on reload while crypto prices load. TallyUp shows your last known prices instantly, then updates to live ones' },
      { type: 'feature', text: 'Light and dark mode switch on the landing page. Your choice carries into the dashboard after you sign in' },
      { type: 'fix', text: 'Retirement Projection now withdraws your pre-tax need when you set a tax rate. Previously the chart and runway ignored taxes and understated withdrawals' },
      { type: 'feature', text: 'Plan retirement by age: add your birth year and type the age you want to retire at, and TallyUp sets the retirement year for you' },
      { type: 'feature', text: 'The landing page now has a short video tour of TallyUp that plays muted as you scroll to it, with a one-tap sound button' },
    ],
  },
  {
    version: '2.6.0',
    date: '2026-09-27',
    items: [
      { type: 'feature', text: 'TallyUp Pro is now a one-time purchase. Pay once and keep Pro for good, with no subscription to manage' },
      { type: 'feature', text: 'Retirement Projection now runs on the free tier, with your nest egg, runway and a 5-year trajectory. Pro charts the full 50 years and the year-by-year breakdown' },
      { type: 'feature', text: 'All four calculators (Compound Interest, FIRE, Retirement, Debt Payoff) are now free for everyone' },
      { type: 'improvement', text: 'Net worth trend and asset allocation are now available on the free dashboard' },
      { type: 'improvement', text: 'Free now shows the last 12 months of net worth history; Pro shows all of it. Nothing is ever removed from your sheet' },
      { type: 'fix', text: 'The upgrade screen no longer advertises crypto tracking and custom categories as Pro, since both have always been free' },
      { type: 'fix', text: 'Fixed Retirement and Calculators showing a padlock in the sidebar and refusing to open' },
      { type: 'improvement', text: 'Pinch to zoom on the Trends chart is calmer on phones: pinch side to side to stretch time, or up and down to stretch the dollar scale' },
    ],
  },
  {
    version: '2.5.2',
    date: '2026-09-25',
    items: [
      { type: 'improvement', text: 'Trends opens in Fit scale (Net Worth focused) so the detailed chart has the same depth as the dashboard widget — use Full when you want $0 and Assets/Liabilities in frame' },
      { type: 'fix', text: 'Trends scroll zoom now grow/shrinks from the center instead of re-anchoring to the cursor — and stays on screen' },
    ],
  },
  {
    version: '2.5.1',
    date: '2026-09-22',
    items: [
      { type: 'feature', text: 'Investments, Crypto, and Private Equity Return columns toggle Total / 1D / 1W / 1M / 1Y' },
      { type: 'fix', text: 'Period returns (1D–1Y) now load from live market data — Google Sheets cannot expose historical GOOGLEFINANCE values through the API' },
    ],
  },
  {
    version: '2.5.0',
    date: '2026-09-22',
    items: [
      { type: 'feature', text: 'Trends MA Cloud — SMA + EMA together with a fill between them (20 / 50 / 200), matching a TradingView-style cloud' },
      { type: 'feature', text: 'Trends RSI pane under the main chart (14-period with overbought / oversold levels)' },
      { type: 'feature', text: 'Trends chart scaling — Full vs Fit depth, scroll to zoom time, Alt/Shift-scroll or $ axis to zoom depth, drag to pan, double-click or Reset to restore' },
    ],
  },
  {
    version: '2.4.0',
    date: '2026-09-21',
    items: [
      { type: 'feature', text: 'Trends chart redesigned with shared Y-axis, dollar labels, crosshair readout, and SMA/EMA overlays (20 / 50 / 200)' },
      { type: 'fix', text: 'Trends liabilities no longer look exaggerated — all series share one scale, and the liability line now includes credit card balances' },
      { type: 'fix', text: 'Investments Total Gain/Loss now includes crypto, 401(k), and private equity — not just stocks' },
      { type: 'fix', text: 'Fixed Market Snapshot custom tickers not saving — adding or removing tickers no longer gets overwritten on reload' },
      { type: 'fix', text: 'Fixed Market Snapshot add flow falsely rejecting valid tickers before Google Finance finished loading' },
      { type: 'fix', text: 'Fixed missing error feedback when adding or removing Market Snapshot tickers without a connected spreadsheet' },
      { type: 'improvement', text: 'Google Sheets sessions stay alive on long-lived tabs so you are not kicked out mid-session' },
      { type: 'fix', text: 'Fixed Google reconnect flow and token refresh UX when your spreadsheet session expires' },
    ],
  },
  {
    version: '2.3.1',
    date: '2026-04-20',
    items: [
      { type: 'fix', text: 'Fixed leading zeros not deleting when typing numbers in the Retirement Calculator' },
      { type: 'fix', text: 'Fixed clipped classification label on the Crypto Fear & Greed gauge' },
    ],
  },
  {
    version: '2.3.0',
    date: '2026-04-19',
    items: [
      { type: 'feature', text: 'Budget subcategories — organize budget items into groups like Housing, Food, Transportation with collapsible headers and subtotals' },
      { type: 'feature', text: 'Screenshot attachments in feedback — attach a screenshot when submitting feedback from Settings' },
      { type: 'improvement', text: 'Retirement projection graph now peaks at retirement year instead of showing withdrawals early' },
      { type: 'improvement', text: 'Onboarding dialogs now properly centered on mobile (uses dynamic viewport height)' },
      { type: 'improvement', text: 'Renamed "Banking Accounts" to "Banking" for cleaner widget titles' },
      { type: 'fix', text: 'Fixed NaN% appearing in the Retirement Calculator when inputs are zero' },
      { type: 'fix', text: 'Fixed retirement settings clearing when switching tabs' },
    ],
  },
  {
    version: '2.2.0',
    date: '2026-03-25',
    items: [
      { type: 'feature', text: 'Auto-refresh on tab return — your data now updates automatically when you switch back to TallyUp after 2+ minutes' },
      { type: 'feature', text: 'Widget visibility toggles — hide widgets you don\'t use (HSA, Credit Cards, etc.) from Settings' },
      { type: 'feature', text: 'Allocation chart drill-down — tap a category to see individual holdings within it' },
      { type: 'feature', text: 'Multi-account CSV import — Schwab "All Accounts" exports are now auto-detected with correct account types' },
      { type: 'feature', text: 'Custom account labels — distinguish accounts like "Nick\'s Roth" or "Jen\'s Roth" via the Other option' },
      { type: 'improvement', text: 'CSV re-import now updates existing holdings instead of creating duplicates' },
      { type: 'improvement', text: 'Added Money Market as an investment account type' },
      { type: 'improvement', text: 'Dashboard URL now shows /dashboard when signed in' },
      { type: 'fix', text: 'Fixed CSV auto-matching for Schwab\'s "Qty (Quantity)" column' },
      { type: 'improvement', text: 'Bulk delete is now instant — selecting multiple items and deleting happens in one batch instead of one at a time' },
      { type: 'fix', text: 'Fixed beta invite code being cleared on every sign-out' },
      { type: 'improvement', text: 'Theme now follows your system appearance (light/dark) automatically' },
      { type: 'improvement', text: 'Improved iPad and tablet layout — header toolbar and snapshot section no longer overflow' },
      { type: 'improvement', text: 'Updated dependencies for improved performance and security' },
      { type: 'feature', text: 'Estimated tax rate field in Retirement Projection — see both gross and after-tax annual income' },
      { type: 'fix', text: 'Retirement settings now persist when switching tabs without requiring a page refresh' },
    ],
  },
  {
    version: '2.1.0',
    date: '2026-03-23',
    items: [
      { type: 'feature', text: 'CSV import now auto-detects the header row — no need to manually remove metadata rows from brokerage exports (e.g. Schwab)' },
      { type: 'feature', text: 'Default account type selector — when importing investments without an Account Type column, pick the account type to apply to all rows' },
      { type: 'improvement', text: 'Added SIMPLE IRA as an investment account type option' },
      { type: 'fix', text: 'Fixed bug where leaving Cost Basis empty when adding an investment could cause the account type to disappear' },
    ],
  },
  {
    version: '2.0.0',
    date: '2026-03-19',
    items: [
      { type: 'improvement', text: 'Redesigned color theme — unified violet/slate palette replaces purple/gray across the entire app' },
      { type: 'improvement', text: 'Landing page refresh — alternating section backgrounds, tighter spacing, and aligned pricing cards' },
      { type: 'improvement', text: 'Dashboard summary cards — color-coded labels matching the accounts tab style' },
      { type: 'improvement', text: 'Modal overlay — now uses a portal for correct layering with adjustable transparency' },
      { type: 'improvement', text: 'Responsive improvements — debt payoff calculator, card headers, and button alignment on mobile' },
      { type: 'fix', text: 'Fixed net worth total on landing page to match line items ($254,100)' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-03-17',
    items: [
      { type: 'feature', text: 'Per-asset CAGR control — set your own estimated growth rate for each individual holding with +/\u2212 buttons' },
      { type: 'feature', text: 'Retirement projection tutorial — step-by-step guide accessible via the help button' },
      { type: 'feature', text: 'Terms of Service acceptance gate — all users review and accept terms on their next login' },
      { type: 'feature', text: 'Local storage consent banner — informs users about data storage with a link to the Privacy Policy' },
      { type: 'improvement', text: 'Growth rates and scenario settings now persist to Google Sheets across sessions' },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-03-10',
    items: [
      { type: 'feature', text: 'Dashboard Layout — choose between Overview, Standard, or Full view to control how much detail you see' },
      { type: 'feature', text: 'Layout selection during onboarding — new users pick their preferred dashboard density after signup' },
      { type: 'feature', text: 'Share TallyUp — share the app with friends via the native share menu (in Settings)' },
      { type: 'improvement', text: 'Total Cost column added to Investment Portfolio and Crypto Holdings tables' },
      { type: 'improvement', text: 'Consistent text sizing across all investment page widget tables' },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-03-08',
    items: [
      { type: 'feature', text: 'Retirement Projection (Pro) — visualize your wealth trajectory with per-asset growth rates and scenario modeling' },
      { type: 'feature', text: 'Dedicated Retirement tab — projection, year-by-year breakdown, and collapsible holdings with custom CAGR' },
      { type: 'feature', text: 'Asset class filter chips — toggle Stocks, Crypto, 401(k), Private Equity, and Cash in your projection' },
      { type: 'improvement', text: 'Renamed Tools tab to Calculators — FIRE, Retirement, Compound Interest, and Debt Payoff calculators are now generic with no portfolio defaults' },
      { type: 'improvement', text: 'Improved number input editing — inflation, CAGR, and spending fields no longer snap to 0 when clearing' },
      { type: 'improvement', text: 'Updated landing page to highlight investor and retirement planning features' },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-03-07',
    items: [
      { type: 'feature', text: 'Financial Tools tab (Pro) — Compound Interest, FIRE, Retirement, and Debt Payoff calculators' },
      { type: 'feature', text: 'Net Worth Milestones — track progress from $10k to $10M with confetti celebrations' },
      { type: 'feature', text: 'Goal units — track goals in BTC, ETH, Shares, or custom units instead of just dollars' },
      { type: 'improvement', text: 'Simplified allocation chart — aggregated by category with consistent colors' },
      { type: 'improvement', text: 'Market Snapshot mobile touch support — swipe with momentum scrolling, tap to pause' },
      { type: 'fix', text: 'Fixed reconnect flash after re-login on mobile' },
      { type: 'fix', text: 'Fixed Charts tab default view persisting when navigating away' },
    ],
  },
  {
    version: '1.3.5',
    date: '2026-03-06',
    items: [
      { type: 'feature', text: 'Charts page command center — each card now shows live KPI data at a glance' },
      { type: 'improvement', text: 'Professional Lucide icons replace emojis throughout the entire app' },
      { type: 'improvement', text: 'Consistent icon badges with themed purple backgrounds in dark and light mode' },
      { type: 'improvement', text: 'Uniform card sizing on the Charts dashboard grid' },
    ],
  },
  {
    version: '1.3.4',
    date: '2026-03-06',
    items: [
      { type: 'feature', text: 'Allocation chart category toggles — show/hide Cash, Stocks, 401k, Crypto, PE, Assets, and HSA' },
      { type: 'feature', text: 'Dollar/Percent toggle on allocation chart — hide dollar amounts for easy sharing' },
      { type: 'feature', text: 'HSA accounts now included in allocation chart' },
      { type: 'improvement', text: 'Duplicate holdings (e.g. SOL, BTC) are now merged in the allocation chart' },
      { type: 'feature', text: 'Leader line labels on pie chart slices with ticker + value' },
      { type: 'feature', text: 'Mini allocation chart preview on Investments tab — tap to jump to full chart' },
    ],
  },
  {
    version: '1.3.3',
    date: '2026-03-05',
    items: [
      { type: 'feature', text: 'Credit Scores tracking — monitor Equifax, TransUnion, Experian, and FICO scores with color-coded ratings' },
    ],
  },
  {
    version: '1.3.2',
    date: '2026-03-03',
    items: [
      { type: 'fix', text: 'Expired Google sessions now auto-refresh — no more "Failed to load" on stale tabs' },
      { type: 'fix', text: 'Reconnect flow no longer causes login loops or shows dashboard prematurely' },
      { type: 'improvement', text: 'Faster and more reliable data loading with smarter retry logic' },
      { type: 'fix', text: 'Net worth history snapshots now use accurate data instead of stale values' },
    ],
  },
  {
    version: '1.3.1',
    date: '2026-03-02',
    items: [
      { type: 'fix', text: 'Tutorial completion now persists to your account so it won\'t replay on new devices' },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-03-01',
    items: [
      { type: 'feature', text: 'Custom PDF export — choose which sections to include in your report' },
      { type: 'feature', text: 'Stripe billing portal — manage subscription, payment methods, and invoices' },
      { type: 'improvement', text: 'Updated Terms of Service and Privacy Policy' },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-02-15',
    items: [
      { type: 'feature', text: 'HSA (Health Savings Account) tracking widget' },
      { type: 'feature', text: 'Private Equity holdings widget' },
      { type: 'improvement', text: 'Improved token refresh and session handling' },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-02-01',
    items: [
      { type: 'feature', text: 'Crypto portfolio tracking with live CoinGecko prices' },
      { type: 'fix', text: 'Fixed alignment issues across dashboard widgets' },
      { type: 'improvement', text: 'Dark mode polish and consistency improvements' },
    ],
  },
];

export const LATEST_VERSION = CHANGELOG[0].version;

const STORAGE_KEY_PREFIX = 'tallyup_last_seen_version';

function getStorageKey(): string {
  // Scope to user so switching accounts shows the badge for each user
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '');
        const userId = data?.user?.id;
        if (userId) return `${STORAGE_KEY_PREFIX}_${userId}`;
      } catch { /* fall through */ }
    }
  }
  return STORAGE_KEY_PREFIX;
}

export function hasUnseenUpdates(): boolean {
  const lastSeen = localStorage.getItem(getStorageKey());
  return lastSeen !== LATEST_VERSION;
}

export function markUpdatesSeen(): void {
  localStorage.setItem(getStorageKey(), LATEST_VERSION);
}
